import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  TreeStructure, Plus, X, Users, Heart, Baby, UserCircle, CheckCircle, Sparkle,
} from "@phosphor-icons/react";

const emptyForm = {
  branch: "",
  parents: ["", ""],
  siblings: [""],
  spouse: "",
  children: [""],
};

function NameRow({ value, onChange, onRemove, placeholder, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-white border-sage focus-visible:ring-gold"
        data-testid={testId}
      />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-sageGray hover:text-destructive p-2 rounded-md hover:bg-cream transition-colors"
          aria-label="Remove"
          data-testid={`${testId}-remove`}
        >
          <X size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}

export default function Onboarding() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [g, m, existing] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/members`),
          api.get(`/groups/${groupId}/onboarding`),
        ]);
        if (cancelled) return;
        setGroup(g.data);
        setMembers(m.data);
        if (existing.data && existing.data.completed_at) {
          // Already completed — pre-fill so they can review
          setForm({
            branch: existing.data.branch || "",
            parents: existing.data.parents?.length ? existing.data.parents : ["", ""],
            siblings: existing.data.siblings?.length ? existing.data.siblings : [""],
            spouse: existing.data.spouse || "",
            children: existing.data.children?.length ? existing.data.children : [""],
          });
        }
      } catch (e) {
        toast.error(formatApiError(e) || "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  if (!group) return <div className="min-h-screen bg-ivory"><TopNav /><div className="p-10 text-sageGray">Loading…</div></div>;

  if (group.status !== "verified") {
    return (
      <div className="min-h-screen bg-ivory grain-bg">
        <TopNav />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="font-display text-3xl text-forest">You're not verified in this circle yet.</h1>
          <p className="text-sageGray mt-3">An admin needs to approve you before you can fill in family info.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="onboarding-back-dashboard">Back to dashboard</Button>
        </main>
      </div>
    );
  }

  const setArr = (key, idx, val) => {
    const next = [...form[key]];
    next[idx] = val;
    setForm({ ...form, [key]: next });
  };
  const addRow = (key) => setForm({ ...form, [key]: [...form[key], ""] });
  const removeRow = (key, idx) => {
    const next = form[key].filter((_, i) => i !== idx);
    setForm({ ...form, [key]: next.length ? next : [""] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        branch: form.branch || null,
        parents: form.parents.filter(Boolean),
        siblings: form.siblings.filter(Boolean),
        spouse: form.spouse.trim(),
        children: form.children.filter(Boolean),
      };
      const { data } = await api.post(`/groups/${groupId}/onboarding`, body);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const SectionHeader = ({ icon: Icon, title, sub }) => (
    <div className="flex items-start gap-3 mb-3">
      <span className="w-9 h-9 rounded-lg bg-forest/5 text-forest flex items-center justify-center shrink-0">
        <Icon size={18} weight="duotone" />
      </span>
      <div>
        <h3 className="font-display text-xl text-forest leading-tight">{title}</h3>
        <p className="text-xs text-sageGray">{sub}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory grain-bg">
      <TopNav />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10 animate-fade-in">
          <p className="text-sm tracking-[0.22em] uppercase text-forest/70 mb-3">Welcome to {group.name}</p>
          <h1 className="font-display text-4xl md:text-5xl text-forest leading-tight">Tell us about your family.</h1>
          <p className="text-sageGray mt-3 max-w-xl">Add the names of your parents, siblings, spouse, and children. If any of them are already in this circle, we'll link you up automatically — no admin needed.</p>
        </div>

        <div className="gold-divider mb-10" />

        <form onSubmit={submit} className="space-y-10" data-testid="onboarding-form">
          {/* Branch */}
          <section className="legacy-card p-7">
            <SectionHeader icon={TreeStructure} title="Family branch" sub="Pick the side of the family you belong to. This helps organize the map." />
            <ToggleGroup
              type="single"
              value={form.branch}
              onValueChange={(v) => setForm({ ...form, branch: v || "" })}
              className="flex flex-wrap gap-2 justify-start"
              data-testid="branch-toggle"
            >
              <ToggleGroupItem value="paternal" className="data-[state=on]:bg-forest data-[state=on]:text-ivory border border-sage rounded-full px-5" data-testid="branch-paternal">Paternal side</ToggleGroupItem>
              <ToggleGroupItem value="maternal" className="data-[state=on]:bg-forest data-[state=on]:text-ivory border border-sage rounded-full px-5" data-testid="branch-maternal">Maternal side</ToggleGroupItem>
              <ToggleGroupItem value="both" className="data-[state=on]:bg-forest data-[state=on]:text-ivory border border-sage rounded-full px-5" data-testid="branch-both">Both / not sure</ToggleGroupItem>
            </ToggleGroup>
          </section>

          {/* Parents */}
          <section className="legacy-card p-7">
            <SectionHeader icon={UserCircle} title="Your parents" sub="Up to two names. We'll match each to existing members." />
            <div className="space-y-3" data-testid="parents-section">
              {form.parents.map((v, i) => (
                <NameRow
                  key={i}
                  value={v}
                  placeholder={i === 0 ? "First parent's full name" : "Second parent's full name"}
                  onChange={(val) => setArr("parents", i, val)}
                  onRemove={form.parents.length > 1 ? () => removeRow("parents", i) : null}
                  testId={`parent-input-${i}`}
                />
              ))}
              {form.parents.length < 2 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => addRow("parents")} className="text-forest hover:bg-cream" data-testid="add-parent-button">
                  <Plus size={14} className="mr-1" weight="bold" /> Add another parent
                </Button>
              )}
            </div>
          </section>

          {/* Siblings */}
          <section className="legacy-card p-7">
            <SectionHeader icon={Users} title="Your siblings" sub="Brothers and sisters who share at least one of the parents above." />
            <div className="space-y-3" data-testid="siblings-section">
              {form.siblings.map((v, i) => (
                <NameRow key={i} value={v} placeholder="Sibling's full name"
                  onChange={(val) => setArr("siblings", i, val)}
                  onRemove={form.siblings.length > 1 ? () => removeRow("siblings", i) : null}
                  testId={`sibling-input-${i}`} />
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={() => addRow("siblings")} className="text-forest hover:bg-cream" data-testid="add-sibling-button">
                <Plus size={14} className="mr-1" weight="bold" /> Add another sibling
              </Button>
            </div>
          </section>

          {/* Spouse */}
          <section className="legacy-card p-7">
            <SectionHeader icon={Heart} title="Your spouse" sub="Leave blank if not applicable." />
            <NameRow value={form.spouse} placeholder="Spouse's full name"
              onChange={(val) => setForm({ ...form, spouse: val })}
              testId="spouse-input" />
          </section>

          {/* Children */}
          <section className="legacy-card p-7">
            <SectionHeader icon={Baby} title="Your children" sub="Add each child by name." />
            <div className="space-y-3" data-testid="children-section">
              {form.children.map((v, i) => (
                <NameRow key={i} value={v} placeholder="Child's full name"
                  onChange={(val) => setArr("children", i, val)}
                  onRemove={form.children.length > 1 ? () => removeRow("children", i) : null}
                  testId={`child-input-${i}`} />
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={() => addRow("children")} className="text-forest hover:bg-cream" data-testid="add-child-button">
                <Plus size={14} className="mr-1" weight="bold" /> Add another child
              </Button>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="border-forest/30 text-forest hover:bg-cream rounded-full h-11 px-6" data-testid="onboarding-skip-button">Skip for now</Button>
            <Button type="submit" disabled={busy} className="bg-forest hover:bg-forestSoft text-ivory rounded-full h-11 px-8" data-testid="onboarding-submit-button">
              {busy ? "Saving…" : "Save & find my connections"}
            </Button>
          </div>
        </form>
      </main>

      {/* Results dialog */}
      <Dialog open={!!result} onOpenChange={(o) => { if (!o) { setResult(null); navigate(`/groups/${groupId}`); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="onboarding-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-forest flex items-center gap-2">
              <Sparkle size={22} weight="fill" className="text-gold" /> Family info saved
            </DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-5 pt-2">
              {result.created.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-forest/70 mb-2">Connections found ({result.created.length})</p>
                  <ul className="space-y-2" data-testid="onboarding-created-list">
                    {result.created.map((c, idx) => (
                      <li key={idx} className="flex items-center gap-2 p-3 bg-cream border border-sage rounded-lg">
                        <CheckCircle size={16} className="text-forest shrink-0" weight="fill" />
                        <span className="text-sm"><strong>{memberById[c.to_user_id]?.name || c.to_name}</strong> linked as your <span className="text-forest font-medium">{c.type}</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.suggested_siblings.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-forest/70 mb-2">Suggested siblings ({result.suggested_siblings.length})</p>
                  <ul className="space-y-2" data-testid="onboarding-suggested-siblings">
                    {result.suggested_siblings.map((s, idx) => (
                      <li key={idx} className="flex items-center gap-2 p-3 bg-gold/10 border border-gold/40 rounded-lg">
                        <Users size={16} className="text-forest shrink-0" weight="fill" />
                        <span className="text-sm"><strong>{memberById[s.to_user_id]?.name || "Member"}</strong> shares a parent with you. Sibling claim added — both of you can verify it.</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.unmatched.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-sageGray mb-2">Saved for later ({result.unmatched.length})</p>
                  <p className="text-xs text-sageGray mb-2">Not in the circle yet — we'll link them up automatically once they join:</p>
                  <ul className="space-y-1.5" data-testid="onboarding-unmatched-list">
                    {result.unmatched.map((u, idx) => (
                      <li key={idx} className="text-sm text-charcoal flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-sageGray" />
                        {u.name} <span className="text-xs text-sageGray">· {u.intended_type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.created.length === 0 && result.suggested_siblings.length === 0 && result.unmatched.length === 0 && (
                <p className="text-sageGray text-sm">No names entered — you can come back any time to fill these in.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setResult(null); navigate(`/groups/${groupId}`); }} className="bg-forest hover:bg-forestSoft text-ivory rounded-full w-full" data-testid="onboarding-result-continue">See the map</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
